import { green } from '@ant-design/colors';
import {
  CheckCircleOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  UnlockOutlined
} from '@ant-design/icons';
import { Button, message, Space, Tag, Tooltip } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import React, { useContext, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ResponsiveContainer from '../../components/ResponsiveContainer';
import TableAndFilters from '../../components/TableAndFilters';
import UserContext from '../../context/user-context';
import { errorHandler, exportCSVFile, gruposSistema } from '../../utils';
import { instanceRef } from '../../utils/axios';
import { fieldTypes } from '../../utils/filter-utils';

const styles = {
  menuAction: {
    marginRight: 10
  }
};

function UsuarioList(props) {
  const { grupos } = useContext(UserContext);
  const { user } = useContext(UserContext);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [userRecovery, setUserRecovery] = useState();
  const history = useHistory();

  const getDescripcionGrupo = (grupo) => {
    const grupoUsuario =
      grupos.length > 0 && grupos.find((item) => item.id === grupo);
    if (grupo) {
      return gruposSistema[grupoUsuario.id];
    }
    return '';
  };

  const showModal = (userParam) => {
    setIsModalVisible(true);
    setUserRecovery(userParam);
  };

  const handleOk = () => {
    setIsModalVisible(false);
    recuperar();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleOpenUrl = (url) => {
    history.push(url);
  };

  const recuperar = async () => {
    try {
      await instanceRef.instance.post('auth/recovery', {
        username: userRecovery.usuario
      });
      message.success('Correo de recuperación enviado correctamente');
    } catch (err) {
      message.error('No se puedo recuperar el usuario');
    }
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'nombres',
      filterType: fieldTypes.STRING,
      sortable: true
    },
    {
      title: 'Usuario',
      dataIndex: 'usuario',
      filterType: fieldTypes.STRING,
      sortable: true
    },
    {
      title: 'Código Interno de Usuario',
      dataIndex: 'usuario_interno',
      filterType: fieldTypes.STRING,
      sortable: true
    },
    {
      title: 'Correo',
      dataIndex: 'correo',
      filterType: fieldTypes.STRING,
      sortable: true
    },
    {
      title: 'F. Nacimiento',
      dataIndex: 'fecha_nacimiento',
      filterType: fieldTypes.DATE,
      sortable: true
    },
    {
      title: 'Empresa',
      filterType: fieldTypes.STRING,
      dataIndex: ['empresa', 'nombre'],
      sortable: true
    },
    {
      title: 'Rol',
      dataIndex: 'grupo',
      filterType: fieldTypes.ENUM,
      filters: Object.keys(gruposSistema).map((key) => ({
        text: gruposSistema[key],
        value: key
      })),
      sortable: true,
      render: (grupo) => getDescripcionGrupo(grupo)
    },
    {
      title: 'Sucursal',
      dataIndex: ['sucursal', 'descripcion'],
      filterType: fieldTypes.STRING,
      sortable: true
    },
    {
      title: 'Ingresó a su cuenta',
      dataIndex: 'logueado',
      key: 'logueado',
      filterKey: 'logueado',
      filterable: true,
      align: 'center',
      render: (logueado) => {
        return (
          <Tag
            color={logueado === 'SI' ? `green` : `volcano`}
            key={logueado}
            style={{ minWidth: '35px' }}
          >
            {logueado || 'NO'}
          </Tag>
        );
      }
    },
    {
      title: 'Acciones',
      key: 'action',
      width: '10px',
      render: (text, record) => (
        <Space size="small">
          <Tooltip title="Ver usuario">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="middle"
              onClick={() => handleOpenUrl(`usuarios/view/${record.id}`)}
            />
          </Tooltip>
          {user &&
            !(
              user['cognito:groups'][0] === 'ADMIN_SUCURSAL' &&
              record.grupo === 'ADMIN_SUCURSAL'
            ) && (
              <Tooltip title="Editar usuario">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  size="middle"
                  onClick={() => handleOpenUrl(`usuarios/form/${record.id}`)}
                />
              </Tooltip>
            )}
          <Tooltip title="Recuperar contraseña">
            <Button
              type="text"
              shape="circle"
              icon={<UnlockOutlined />}
              size="middle"
              onClick={() => showModal(record)}
            />
          </Tooltip>
          {user.sub !== record.id_cognito ? (
            <Tooltip title={record.activo ? 'Desactivar' : 'Activar'}>
              <Button
                type="text"
                style={record.activo ? { color: green.primary } : {}}
                danger={!record.activo}
                icon={<CheckCircleOutlined />}
                size="middle"
                loading={loadingState === record.id}
                onClick={() => toggle(record)}
              />
            </Tooltip>
          ) : (
            <Tooltip title={record.activo ? 'Activo' : 'Desactivado'} />
          )}
        </Space>
      ),
      align: 'left'
    }
  ];

  const [loadingState, setLoadingState] = useState(0);
  const tableRef = useRef();

  const toggle = async (record) => {
    try {
      setLoadingState(record.id);
      if (record.activo) {
        await instanceRef.instance.post('auth/disable', {
          username: record.usuario
        });
      } else {
        await instanceRef.instance.post('auth/enable', {
          username: record.usuario
        });
      }
      tableRef.current.reload();
    } catch (error) {
      errorHandler(error);
    }
    setLoadingState(0);
  };

  // TODO: Terminar de revisar datos exportados
  const getExportData = async (range) => {
    try {
      const response = await instanceRef.instance.get('auth/users', {
        params: {
          filters: { range }
        }
      });
      const itemsFormatted = [];
      response.data.data.forEach((item) => {
        itemsFormatted.push({
          nombres: item.nombres,
          usuario: item.usuario,
          usuario_interno: item.usuario_interno,
          correo: item.correo,
          grupo: getDescripcionGrupo(item.grupo),
          sucursal: item.sucursal?.descripcion || ''
        });
      });
      // call the exportCSVFile() function to process the JSON and trigger the download
      exportCSVFile(
        {
          nombres: 'nombres',
          usuario: 'usuario',
          usuario_interno: 'usuario_interno',
          correo: 'correo',
          grupo: 'grupo',
          sucursal: 'sucursal'
        },
        itemsFormatted,
        'usuarios'
      );
    } catch (error) {
      // error
    }
  };

  return (
    <ResponsiveContainer size="large" style={{ paddingTop: 0 }}>
      <div style={{ overflow: 'auto' }}>
        <TableAndFilters
          ref={tableRef}
          withGeneralFilter
          url="/auth/users"
          title="Usuarios"
          columns={columns}
          hasExportar
          getExportData={getExportData}
          extra={[
            <Button
              key="2"
              type="dashed"
              shape="round"
              icon={<PlusOutlined />}
              size="middle"
              onClick={() => props.history.push(`usuarios/form`)}
              style={styles.menuAction}
            >
              Agregar
            </Button>
          ]}
        />
      </div>
      <Modal
        title="Recuperación de contraseña"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Enviar"
        cancelText="Cancelar"
      >
        <p>Se enviará un correo de recuperación de contraseña al usuario</p>
      </Modal>
    </ResponsiveContainer>
  );
}

export default UsuarioList;
